import { useState } from "react";
import { Wind } from "lucide-react";
import { BRIGHT_BG, HATCH, SLIDE_MS, useCycle } from "@/components/broadcast/shared";
import { API } from "@/lib/api";

function tropTheme(s) {
  const c = s.classification;
  if (c === "MH") return { accent: "#7a0416", light: false };
  if (c === "HU") return { accent: "#d90429", light: false };
  if (c === "TS" || c === "STS") return { accent: "#f6a21c", light: true };
  if (c === "TD" || c === "STD") return { accent: "#1f63c9", light: false };
  return { accent: "#2a9d8f", light: false };
}
function tropMonitor(s) {
  const d = s.dist_mi;
  if (d == null) return "GAWX is tracking this system.";
  if (d > 1200) return "No direct threat to Georgia at this time — GAWX is monitoring.";
  if (d > 600) return "Still well offshore — Georgia should stay weather-aware.";
  return "Within range of Georgia — monitor closely for watches & warnings.";
}
function ConeImage({ src, className, fallbackLabel }) {
  const [err, setErr] = useState(false);
  if (err || !src) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-center text-white/70">
        <Wind className="h-24 w-24" />
        <div className="font-oswald text-[22px] font-bold uppercase tracking-[0.2em]">{fallbackLabel || "Forecast cone updating"}</div>
      </div>
    );
  }
  return <img src={src} className={className} alt="NHC 5-day forecast cone" onError={() => setErr(true)} />;
}
function TropicalUpdateSlide({ systems = [], perMs = SLIDE_MS }) {
  const k = useCycle(systems.length, perMs);
  const s = systems[k];
  if (!s) return null;
  const { accent, light } = tropTheme(s);
  const ink = light ? "#0a0f1c" : "#fff";
  const eyebrow = s.category ? `Category ${s.category} Hurricane` : s.kind;
  const stats = [
    ["Max winds", s.wind_mph != null ? `${s.wind_mph} mph` : "—"],
    ["Min pressure", s.pressure_mb != null ? `${s.pressure_mb} mb` : "—"],
    ["Moving", s.movement || "—"],
    ["Position", s.position || "—"],
    ["From Atlanta", s.dist_mi != null ? `${s.dist_mi} mi ${s.bearing}` : "—"],
    ["Basin", s.basin || "Atlantic"],
  ];
  return (
    <div data-testid="tropical-update-slide" className="relative h-full w-full overflow-hidden" style={{ background: BRIGHT_BG }}>
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: HATCH }} />
      <div key={`tbg-${s.id}-${k}`} className="absolute inset-0">
        <div className="alert-wipe absolute inset-y-0 left-0 w-[52%]" style={{ background: `linear-gradient(160deg, ${accent} 0%, ${accent}cc 100%)`, clipPath: "polygon(0 0, 100% 0, 88% 100%, 0 100%)" }} />
        <div className="alert-wipe absolute inset-y-0 left-[48%] w-[2.5%] bg-[#0a0f1c]" style={{ clipPath: "polygon(60% 0, 100% 0, 40% 100%, 0 100%)", animationDelay: "120ms" }} />
      </div>
      <div key={`tc-${s.id}-${k}`} className="relative flex h-full w-full items-stretch gap-10 px-[70px] pb-[64px]">
        <div className="flex min-w-0 flex-1 flex-col justify-center" style={{ color: ink }}>
          <div className="flex items-center gap-3">
            <div className="inline-flex w-fit items-center gap-3 rounded-full bg-black/30 px-5 py-2 font-oswald text-[22px] font-bold uppercase tracking-[0.3em] text-white">
              <span className="live-dot" style={{ width: 12, height: 12 }} /> NHC · Tropical Update
            </div>
            {systems.length > 1 && (
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0a0f1c] px-4 py-2 font-oswald text-[20px] font-bold uppercase tracking-[0.2em] text-white" data-testid="tropical-counter">
                System {k + 1} <span className="opacity-50">of</span> {systems.length}
              </div>
            )}
          </div>
          <div className="mt-4 font-oswald text-[30px] font-bold uppercase tracking-[0.2em]" style={{ opacity: 0.9 }}>{eyebrow}</div>
          <div className="font-oswald text-[112px] font-black uppercase leading-[0.85] tracking-wide drop-shadow-[0_10px_30px_rgba(0,0,0,0.25)]" data-testid="tropical-update-name">{s.name}</div>
          <div className="mt-6 grid max-w-[860px] grid-cols-3 gap-3">
            {stats.map(([kk, vv]) => (
              <div key={kk} className="rounded-[5px] bg-white px-5 py-3 shadow-[0_10px_30px_rgba(10,15,28,0.14)]">
                <div className="font-oswald text-[20px] font-bold uppercase tracking-[0.2em] text-[#5b6675]">{kk}</div>
                <div className="mt-0.5 font-oswald text-[32px] font-black leading-none text-[#0a0f1c]">{vv}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 w-fit max-w-[860px] rounded-[6px] bg-[#0a0f1c] px-7 py-4 text-white">
            <div className="font-oswald text-[21px] font-bold uppercase tracking-[0.3em]" style={{ color: "#ffd60a" }}>Georgia outlook</div>
            <div className="mt-1 font-oswald text-[30px] font-black uppercase leading-tight">{tropMonitor(s)}</div>
          </div>
        </div>
        <div className="flex w-[560px] shrink-0 flex-col justify-center">
          <div className="overflow-hidden rounded-[6px] bg-white shadow-[0_30px_80px_rgba(10,15,28,0.3)]">
            <div className="flex items-center justify-between px-6 py-3 font-oswald text-[20px] font-black uppercase tracking-[0.25em] text-white" style={{ background: "#0a0f1c" }}>
              <span>5-Day Forecast Cone</span>
              <span className="flex items-center gap-2 text-[16px] font-bold tracking-widest text-white/70"><span className="live-dot" /> NHC</span>
            </div>
            <div className="relative aspect-square w-full" style={{ background: "#0a1a3a" }}>
              <ConeImage src={`${API}/tropical/cone?id=${s.id}`} className="absolute inset-0 h-full w-full object-contain p-2" fallbackLabel="Cone not yet issued" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TropicalConeSlide({ systems = [], perMs = SLIDE_MS }) {
  const k = useCycle(systems.length, perMs);
  const s = systems[k];
  if (!s) return null;
  const { accent } = tropTheme(s);
  const windLine = s.category ? `Category ${s.category} · ${s.wind_mph} mph` : `${s.kind} · ${s.wind_mph} mph`;
  return (
    <div data-testid="tropical-cone-slide" className="relative h-full w-full overflow-hidden atmo-slide atmo-dusk">
      <div className="absolute inset-0 flex items-center justify-center px-6 pb-[70px] pt-[150px]">
        <ConeImage src={`${API}/tropical/cone?id=${s.id}`} className="h-full w-full object-contain drop-shadow-[0_12px_40px_rgba(0,0,0,0.5)]" fallbackLabel={`${s.name} · cone not yet issued`} />
      </div>
      {systems.length > 1 && (
        <div className="absolute right-6 top-[150px] z-20 inline-flex items-center gap-2 rounded-full bg-[#0a0f1c] px-4 py-2 font-oswald text-[18px] font-bold uppercase tracking-[0.2em] text-white" data-testid="tropical-cone-counter">
          System {k + 1} <span className="opacity-50">of</span> {systems.length}
        </div>
      )}
      <div className="pointer-events-none absolute bottom-10 left-6 z-20 rounded-[4px] border-l-[6px] bg-black/75 px-5 py-3 backdrop-blur-sm" style={{ borderColor: accent }}>
        <div className="font-oswald text-[22px] font-black uppercase tracking-wide text-white">{s.name}</div>
        <div className="font-oswald text-[15px] font-bold uppercase tracking-[0.2em]" style={{ color: accent === "#f6a21c" ? "#ffd60a" : "#fca5a5" }}>{windLine} · {s.movement}</div>
      </div>
      <div className="pointer-events-none absolute bottom-10 right-6 z-20 rounded border border-white/15 bg-black/70 px-3 py-2 backdrop-blur-sm">
        <div className="font-oswald text-[10px] uppercase tracking-[0.3em] text-[#ffd60a]">Source</div>
        <div className="font-oswald text-xs font-bold uppercase tracking-widest text-white">NHC · 5-Day Cone</div>
      </div>
    </div>
  );
}
const RISK_COLOR = { Low: "#eab308", Medium: "#f59e0b", High: "#d90429" };
function TropicalOutlookSlide({ disturbances = [] }) {
  const areas = disturbances.slice(0, 3);
  const accent = "#2a9d8f";
  return (
    <div data-testid="tropical-outlook-slide" className="relative h-full w-full overflow-hidden" style={{ background: BRIGHT_BG }}>
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: HATCH }} />
      <div className="absolute inset-0">
        <div className="alert-wipe absolute inset-y-0 left-0 w-[52%]" style={{ background: `linear-gradient(160deg, ${accent} 0%, ${accent}cc 100%)`, clipPath: "polygon(0 0, 100% 0, 88% 100%, 0 100%)" }} />
        <div className="alert-wipe absolute inset-y-0 left-[48%] w-[2.5%] bg-[#0a0f1c]" style={{ clipPath: "polygon(60% 0, 100% 0, 40% 100%, 0 100%)", animationDelay: "120ms" }} />
      </div>
      <div className="relative flex h-full w-full items-stretch gap-10 px-[70px] pb-[64px]">
        <div className="flex min-w-0 flex-1 flex-col justify-center text-white">
          <div className="inline-flex w-fit items-center gap-3 rounded-full bg-black/30 px-5 py-2 font-oswald text-[22px] font-bold uppercase tracking-[0.3em] text-white">
            <span className="live-dot" style={{ width: 12, height: 12 }} /> NHC · Tropical Weather Outlook
          </div>
          <div className="mt-3 font-oswald text-[80px] font-black uppercase leading-[0.9] tracking-wide drop-shadow-[0_10px_30px_rgba(0,0,0,0.25)]">Areas to Watch</div>
          <div className="mt-6 flex flex-col gap-3" data-testid="tropical-outlook-areas">
            {areas.map((d, i) => {
              const c7 = RISK_COLOR[d.risk7day] || "#eab308";
              const c2 = RISK_COLOR[d.risk2day] || "#eab308";
              const bar = (pct, col) => (
                <div className="mt-1 h-[10px] w-full overflow-hidden rounded-full bg-[#eef2f7]">
                  <div className="h-full rounded-full" style={{ width: `${Math.max(3, pct || 0)}%`, background: col }} />
                </div>
              );
              return (
                <div key={i} className="rounded-[6px] bg-white px-6 py-4 shadow-[0_12px_30px_rgba(10,15,28,0.16)]" data-testid={`tropical-area-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-oswald text-[30px] font-black uppercase leading-none tracking-wide text-[#0a0f1c]">{d.region}</div>
                    <span className="rounded-full px-4 py-1 font-oswald text-[18px] font-black uppercase tracking-widest text-white" style={{ background: c7 }}>{d.risk7day || "Low"} chance</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-6">
                    <div>
                      <div className="flex items-baseline justify-between"><span className="font-oswald text-[20px] font-bold uppercase tracking-[0.2em] text-[#5b6675]">48-hour</span><span className="font-oswald text-[36px] font-black leading-none text-[#0a0f1c]">{d.prob2day != null ? `${d.prob2day}%` : "—"}</span></div>
                      {bar(d.prob2day, c2)}
                    </div>
                    <div>
                      <div className="flex items-baseline justify-between"><span className="font-oswald text-[20px] font-bold uppercase tracking-[0.2em] text-[#5b6675]">7-day</span><span className="font-oswald text-[36px] font-black leading-none text-[#0a0f1c]">{d.prob7day != null ? `${d.prob7day}%` : "—"}</span></div>
                      {bar(d.prob7day, c7)}
                    </div>
                  </div>
                </div>
              );
            })}
            {disturbances.length > 3 && (
              <div className="font-oswald text-[20px] font-bold uppercase tracking-[0.2em] text-white/90">+{disturbances.length - 3} more area{disturbances.length - 3 > 1 ? "s" : ""} being watched</div>
            )}
          </div>
          <div className="mt-6 w-fit max-w-[820px] rounded-[6px] bg-[#0a0f1c] px-7 py-3 font-oswald text-[24px] font-black uppercase leading-tight text-white">GAWX is monitoring the tropics for development</div>
        </div>
        <div className="flex w-[560px] shrink-0 flex-col justify-center">
          <div className="overflow-hidden rounded-[6px] bg-white shadow-[0_30px_80px_rgba(10,15,28,0.3)]">
            <div className="flex items-center justify-between px-6 py-3 font-oswald text-[20px] font-black uppercase tracking-[0.25em] text-white" style={{ background: "#0a0f1c" }}>
              <span>7-Day Outlook</span>
              <span className="flex items-center gap-2 text-[16px] font-bold tracking-widest text-white/70"><span className="live-dot" /> NHC</span>
            </div>
            <div className="relative aspect-[1.25] w-full" style={{ background: "#0a1a3a" }}>
              <ConeImage src={`${API}/tropical/outlook-image`} className="absolute inset-0 h-full w-full object-contain p-2" fallbackLabel="Outlook map updating" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { TropicalUpdateSlide, TropicalConeSlide, TropicalOutlookSlide };
